import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "interfaces/api.interface";
import { IRule, IRuleCondition, RuleCondition } from "models/rule/rule.model";
import { IVirtualWarehouse } from "models/virtualWarehouse/virtualWarehouse.model";
import { createRule, suggestRules, updateRule } from "services/api/rules";
import { ISuggestedRule } from "services/api/rules/dtos/suggest-rules.dto";
import {
  CreateRuleAppliesTo,
  CreateRuleValidationSchema,
} from "services/api/rules/dtos/create-rule.request.dto";
import { CreateRuleAction } from "services/api/rules/dtos/create-rule.request.dto";
import { AppThunk, RootState } from "store";
import { closeModal } from "store/modalSlice";
import { isModalClosed } from "store/modalSlice/modalSlice.actions";
import { modals } from "store/modalSlice/modalSlice.types";
import { fetchRulesThunk } from "store/rulesSlice/index.slice";

interface ICreateOrEditRuleSliceValues {
  name: string;
  virtualWarehouseId: IVirtualWarehouse["id"] | null;
  carrierId: string | null;
  appliesTo: CreateRuleAppliesTo;
  action: CreateRuleAction;
  conditions: RuleCondition[];
  shippingWhitelistId: string | null;
  importerOfRecordSwitch: boolean;
}

type ICreateOrEditRuleSliceErrors = {
  [key in keyof ICreateOrEditRuleSliceValues]: string;
};

interface ICreateOrEditRuleSlice {
  values: ICreateOrEditRuleSliceValues;
  errors: ICreateOrEditRuleSliceErrors;
  touched: any;
  submittingStatus: ApiLoadingStatus;
  editingRuleUUID: IRule["uuid"] | null;
  suggestingStatus: ApiLoadingStatus;
  suggestions: ISuggestedRule[];
  suggestionWarnings: string[];
  suggestionError: string;
}

export const DEFAULT_CONDITION: RuleCondition = {
  operator: null,
  value: "",
  fact: null,
};

const CreateOrEditRuleSliceInitialState: ICreateOrEditRuleSlice = {
  values: {
    name: "",
    virtualWarehouseId: null,
    appliesTo: CreateRuleAppliesTo.ALL,
    conditions: [{ ...DEFAULT_CONDITION }],
    carrierId: null,
    shippingWhitelistId: null,
    action: CreateRuleAction.ASSIGN,
    importerOfRecordSwitch: false,
  },
  errors: {
    name: "",
    virtualWarehouseId: "",
    appliesTo: "",
    conditions: "",
    carrierId: "",
    shippingWhitelistId: "",
    action: "",
    importerOfRecordSwitch: "",
  },
  touched: "",
  submittingStatus: "IDLE",
  editingRuleUUID: null,
  suggestingStatus: "IDLE",
  suggestions: [],
  suggestionWarnings: [],
  suggestionError: "",
};

export const createOrEditRuleSlice = createSlice({
  name: "createOrEditRuleSlice",
  initialState: CreateOrEditRuleSliceInitialState,
  reducers: {
    setSubmittingStatus: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["submittingStatus"]>
    ) => {
      state.submittingStatus = action.payload;
    },
    setEditingRuleUUID: (state, action: PayloadAction<IRule["uuid"]>) => {
      state.editingRuleUUID = action.payload;
    },
    setAppliesTo: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["values"]["appliesTo"]>
    ) => {
      state.values.appliesTo = action.payload;
    },
    setAction: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["values"]["action"]>
    ) => {
      state.values.action = action.payload;
    },
    setName: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["values"]["name"]>
    ) => {
      state.values.name = action.payload;
    },
    setVirtualWarehouseId: (
      state,
      action: PayloadAction<
        ICreateOrEditRuleSlice["values"]["virtualWarehouseId"]
      >
    ) => {
      state.values.virtualWarehouseId = action.payload;

      if (state.values.shippingWhitelistId) {
        state.values.shippingWhitelistId = null;
        state.values.carrierId = null;
      }
    },
    setConditions: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["values"]["conditions"]>
    ) => {
      state.values.conditions = action.payload;
    },
    setShippingWhitelistId: (
      state,
      action: PayloadAction<
        ICreateOrEditRuleSlice["values"]["shippingWhitelistId"]
      >
    ) => {
      state.values.shippingWhitelistId = action.payload;
    },
    setImporterOfRecordSwitch: (
      state,
      action: PayloadAction<
        ICreateOrEditRuleSlice["values"]["importerOfRecordSwitch"]
      >
    ) => {
      state.values.importerOfRecordSwitch = action.payload;
    },
    setCarrierId: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["values"]["carrierId"]>
    ) => {
      state.values.carrierId = action.payload;
    },
    setErrors: (
      state,
      action: PayloadAction<ICreateOrEditRuleSlice["errors"]>
    ) => {
      state.errors = action.payload;
    },
    updateCondition: (
      state,
      action: PayloadAction<{ index: number; updatedCondition: IRuleCondition }>
    ) => {
      state.values.conditions = [
        ...state.values.conditions,
        { ...DEFAULT_CONDITION },
      ];
    },
    addCondition: (state, action: PayloadAction<number>) => {
      state.values.conditions = [
        ...state.values.conditions,
        { ...DEFAULT_CONDITION },
      ];
    },
    deleteCondition: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0)
        state.values.conditions = [...state.values.conditions].splice(
          action.payload,
          1
        );
    },
    setSuggestingStatus: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.suggestingStatus = action.payload;
    },
    setSuggestions: (
      state,
      action: PayloadAction<{
        suggestions: ISuggestedRule[];
        warnings: string[];
      }>
    ) => {
      state.suggestions = action.payload.suggestions;
      state.suggestionWarnings = action.payload.warnings;
    },
    setSuggestionError: (state, action: PayloadAction<string>) => {
      state.suggestionError = action.payload;
    },
    // Prefills the form the same way EditRuleModal does for an existing rule.
    // The suggestion is only a draft — saving still goes through saveRuleThunk
    // and its validation.
    applySuggestedRule: (state, action: PayloadAction<ISuggestedRule>) => {
      const suggestion = action.payload;

      state.values.name = suggestion.name;
      state.values.virtualWarehouseId = suggestion.virtualWarehouseId;
      state.values.carrierId = suggestion.carrierId;
      state.values.shippingWhitelistId = suggestion.shippingWhitelistId;
      state.values.conditions =
        suggestion.conditions.length > 0
          ? suggestion.conditions
          : [{ ...DEFAULT_CONDITION }];
      state.values.appliesTo =
        suggestion.conditions.length > 0
          ? CreateRuleAppliesTo.CONDITIONAL
          : CreateRuleAppliesTo.ALL;
      state.values.action = suggestion.blockShipmentCreation
        ? CreateRuleAction.BLOCK
        : suggestion.putIntoToFix
        ? CreateRuleAction.PUT_INTO_TO_FIX
        : CreateRuleAction.ASSIGN;
      state.values.importerOfRecordSwitch = false;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isModalClosed,
      (state, action: { type; payload: { modalName: modals } }) => {
        if (
          action.payload.modalName === "editRule" ||
          action.payload.modalName === "createRule"
        ) {
          Object.assign(state, CreateOrEditRuleSliceInitialState);
        }
      }
    );
  },
});

export const {
  setAppliesTo,
  setAction,
  setEditingRuleUUID,
  setSubmittingStatus,
  setShippingWhitelistId,
  deleteCondition,
  setErrors,
  setConditions,
  addCondition,
  updateCondition,
  setName,
  setVirtualWarehouseId,
  setCarrierId,
  setImporterOfRecordSwitch,
  setSuggestingStatus,
  setSuggestions,
  setSuggestionError,
  applySuggestedRule,
} = createOrEditRuleSlice.actions;

export const suggestRulesThunk = (description: string): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (!salesAccountUUID) return;

    dispatch(setSuggestionError(""));
    dispatch(setSuggestions({ suggestions: [], warnings: [] }));
    dispatch(setSuggestingStatus("LOADING"));

    const { data, error, message } = await suggestRules(salesAccountUUID, {
      description,
    });

    if (error || !data) {
      dispatch(
        setSuggestionError(
          typeof message === "string" && message
            ? message
            : "Could not suggest rules — please try again."
        )
      );
    } else {
      dispatch(
        setSuggestions({
          suggestions: data.suggestions || [],
          warnings: data.warnings || [],
        })
      );
    }

    dispatch(setSuggestingStatus("IDLE"));
  };
};

export const saveRuleThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
      createOrEditRuleSlice: { editingRuleUUID, values, errors },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const {
        shippingWhitelistId,
        conditions,
        appliesTo,
        action,
        name,
        virtualWarehouseId,
        importerOfRecordSwitch,
      } = values;

      const schema = CreateRuleValidationSchema;

      let updatedErrors = { ...errors };

      let isValid = true;

      try {
        schema.validateSync(values, { abortEarly: false });
      } catch (err: any) {
        err.inner &&
          err.inner.forEach((e: { path: string; message: string }) => {
            updatedErrors[e.path] = e.message;
            console.log("e.message", e.message);
          });

        isValid = false;
      }

      console.log("isValid", isValid);
      if (!isValid) {
        dispatch(setErrors(updatedErrors));
      } else {
        dispatch(setSubmittingStatus("LOADING"));

        const isEditing = editingRuleUUID;

        const payload = {
          conditions: appliesTo === CreateRuleAppliesTo.ALL ? [] : conditions,
          name,
          virtualWarehouseId,
          shippingWhitelistId: shippingWhitelistId || "",
          blockShipmentCreation: action === CreateRuleAction.BLOCK,
          putIntoToFix: action === CreateRuleAction.PUT_INTO_TO_FIX,
          useImporterOfRecord: importerOfRecordSwitch,
        };

        const { error } = isEditing
          ? await updateRule(salesAccountUUID, editingRuleUUID, payload)
          : await createRule(salesAccountUUID, payload);

        if (!error) {
          dispatch(closeModal({ modalName: "editRule" }));
          dispatch(closeModal({ modalName: "createRule" }));
          dispatch(fetchRulesThunk());
        }

        dispatch(setSubmittingStatus("IDLE"));
      }
    }
  };
};
